import { useEffect, useRef, TransitionEvent, Suspense, lazy } from 'react';
import classNames from 'classnames';

import { sendEventV2 } from '@/utils/analytics';
import { useAppDispatch, useAppSelector } from '@/state/store';
import { useAudio } from '@/hooks/audio';
import { useTheme } from '@/theme';
import { setIsInformationOpen } from '@/state/features/app';
import IconButton from '@/components/IconButton';

import * as styles from './styles.module.scss';

export default function InformationDrawer() {
    const dispatch = useAppDispatch();
    const { audio } = useAudio();
    const theme = useTheme();
    const isInformationOpen = useAppSelector((state) => state.app.isInformationOpen);
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const drawerData = theme.customJSON?.informationDrawer;

    useEffect(() => {
        if (isInformationOpen) {
            audio?.play('gust-open');
            sendEventV2('information_drawer_viewed');
        }
    }, [isInformationOpen]);

    // Move focus to drawer when opened
    function onTransitionEnd(e: TransitionEvent<HTMLDivElement>) {
        if (isInformationOpen && e.target === e.currentTarget) {
            setTimeout(() => {
                closeButtonRef.current?.focus();
            }, 200);
        }
    }

    // Import custom content per instance
    const InfoDrawerContent = lazy(() => {
        switch (process.env.PUBLIC_INSTANCE) {
            case 'rana':
                return import('./content/information-drawer-rana');
            case 'mimas':
                return import('./content/information-drawer-mimas');
            case 'umbra':
                return import('./content/information-drawer-umbra');
            default:
                return Promise.resolve({ default: () => null });
        }
    });

    if (!drawerData?.enabled) return null;
    return (
        <div
            className={classNames(styles.InformationDrawer, {
                [styles.isOpen]: isInformationOpen,
            })}
            inert={!isInformationOpen ? '' : undefined}
            onTransitionEnd={onTransitionEnd}
        >
            <div
                className={styles.scrollContainer}
                style={{ backgroundImage: `url("${drawerData?.topBackgroundImageUrl}")` }}
            >
                <IconButton
                    buttonRef={closeButtonRef}
                    iconType="close"
                    buttonStyle="round"
                    ariaLabel="Close Information"
                    className={styles.closeButton}
                    onClick={() => {
                        audio?.play('gust-close');
                        dispatch(setIsInformationOpen(false));
                    }}
                />
                <div className={styles.contentContainer}>
                    <Suspense fallback={<div>Loading</div>}>
                        <InfoDrawerContent />
                    </Suspense>
                </div>
            </div>
        </div>
    );
}
